import { inject, Injectable, Signal, Type } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CommonApiRequest, CommonGenericModel, Filter, Pagination } from '@iot-platform/models/common';
import { I4BGrid, I4BGridData, I4BGridOptions, I4BGridSort } from '@iot-platform/models/grid-engine';
import { Store } from '@ngrx/store';
import { FilterModel } from 'ag-grid-community';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { getGridApiRequestByGrid } from '../../helpers/utils';
import { CustomGridOptions } from '../../models/custom-grid-options.model';
import { AbstractGridApiHandlerService } from '../../services/abstract-grid-api-handler.service';
import { GridsDbActions } from '../actions';
import * as fromGrids from '../reducers';

@Injectable({
  providedIn: 'root'
})
export class GridEngineFacade {
  private readonly store: Store = inject(Store);
  selectedGridByMasterView: Signal<I4BGrid<I4BGridOptions, I4BGridData>> = this.store.selectSignal(fromGrids.selectSelectedGridByMasterView);
  gridDataLoadedByGridId: Signal<boolean> = this.store.selectSignal(fromGrids.selectGridDataLoadedByGridId);
  selectedGridId: Signal<string> = this.store.selectSignal(fromGrids.getSelectedGridId);
  selectRefreshActivated: Signal<boolean> = this.store.selectSignal(fromGrids.selectRefreshActivated);
  gridDataLoadingByGridId: Signal<boolean> = this.store.selectSignal(fromGrids.selectGridDataLoadingByGridId);
  gridSortByGridId: Signal<I4BGridSort[]> = this.store.selectSignal(fromGrids.selectGridSortByGridId);
  selectedMasterView: Signal<string> = this.store.selectSignal(fromGrids.selectSelectedMasterView);
  rowGroupsState: Signal<any> = this.store.selectSignal(fromGrids.selectRowGroupsState);
  filterState: Signal<any> = this.store.selectSignal(fromGrids.selectFilterState);
  previousUrl: Signal<string> = this.store.selectSignal(fromGrids.selectPreviousUrl);
  selectedItemInGrid$: Observable<CommonGenericModel | undefined> = this.store.select(fromGrids.getSelectedItemInSelectedGrid);
  selectedItemInGrid: Signal<CommonGenericModel | undefined> = this.store.selectSignal(fromGrids.getSelectedItemInSelectedGrid);
  selectedGrid = this.store.selectSignal(fromGrids.getSelectedGrid);
  customGridOptions: Signal<{
    [masterView: string]: {
      apiHandlerService: Type<AbstractGridApiHandlerService>;
    };
  }> = this.store.selectSignal(fromGrids.selectCustomGridOptions);
  allGrids = this.store.selectSignal(fromGrids.getAllGrids);
  allGrids$ = toObservable(this.allGrids);
  defaultSitesGrid = this.store.selectSignal(fromGrids.getDefaultSitesGrid);
  defaultSitesGrid$ = toObservable(this.defaultSitesGrid);
  siteGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  } | null> = this.store.selectSignal(fromGrids.selectSiteGridsConfiguration);
  siteGridsConfiguration$ = toObservable(this.siteGridsConfiguration);
  sitesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.getSitesGrids);
  sitesGrids$ = toObservable(this.sitesGrids);
  defaultStockSiteDevicesGrid = this.store.selectSignal(fromGrids.selectDefaultStockSiteDevicesGrid);
  assetsGrids = this.store.selectSignal(fromGrids.getAssetsGrids);
  assetGridsConfiguration = this.store.selectSignal(fromGrids.selectAssetGridsConfiguration);
  defaultAssetsGrid = this.store.selectSignal(fromGrids.getDefaultAssetsGrid);
  defaultAssetsGrid$ = toObservable(this.defaultAssetsGrid);
  assetEventsBySiteGrids = this.store.selectSignal(fromGrids.getAssetEventsBySiteGrids);
  assetEventBySiteGridsConfiguration = this.store.selectSignal(fromGrids.selectAssetEventBySiteGridsConfiguration);
  assetEventsByAssetGrids = this.store.selectSignal(fromGrids.getAssetEventsByAssetGrids);
  assetEventByAssetGridsConfiguration = this.store.selectSignal(fromGrids.selectAssetEventByAssetGridsConfiguration);
  assetEventsByPERuleGrids = this.store.selectSignal(fromGrids.getAssetEventsByPERuleGrids);
  assetEventByPERuleGridsConfiguration = this.store.selectSignal(fromGrids.selectAssetEventByPERuleGridsConfiguration);
  activeAssetEventsPopupGrids = this.store.selectSignal(fromGrids.getActiveAssetEventsPopupGrids);
  activeAssetEventPopupGridsConfiguration = this.store.selectSignal(fromGrids.selectActiveAssetEventPopupGridsConfiguration);
  assetEventGridsConfiguration = this.store.selectSignal(fromGrids.selectAssetEventGridsConfiguration);
  assetEventsGrids = this.store.selectSignal(fromGrids.getAssetEventsGrids);
  defaultAssetEventsGrid = this.store.selectSignal(fromGrids.getDefaultAssetEventsGrid);
  assetTemplateGrids = this.store.selectSignal(fromGrids.selectAssetTemplateGrids);
  assetTemplateGridsConfiguration = this.store.selectSignal(fromGrids.selectAssetTemplateGridsConfiguration);
  devicesGrids$ = this.store.select(fromGrids.getDevicesGrids);
  devicesGrids = this.store.selectSignal(fromGrids.getDevicesGrids);
  defaultDeviceGrid$ = this.store.select(fromGrids.getDefaultDevicesGrid);
  defaultDeviceGrid = this.store.selectSignal(fromGrids.getDefaultDevicesGrid);
  deviceGridConfiguration = this.store.selectSignal(fromGrids.selectDeviceGridsConfiguration);
  deviceGridConfiguration$ = this.store.select(fromGrids.selectDeviceGridsConfiguration);
  deviceEventsByDeviceGrids = this.store.selectSignal(fromGrids.getDeviceEventsByDeviceGrids);
  deviceEventByDeviceGridsConfiguration = this.store.selectSignal(fromGrids.selectDeviceEventByDeviceGridsConfiguration);
  deviceEventsByPERuleGrids = this.store.selectSignal(fromGrids.getDeviceEventsByPERuleGrids);
  deviceEventByPERuleGridsConfiguration = this.store.selectSignal(fromGrids.selectDeviceEventByPERuleGridsConfiguration);
  deviceEventsBySiteGrids = this.store.selectSignal(fromGrids.getDeviceEventsBySiteGrids);
  deviceEventBySiteGridsConfiguration = this.store.selectSignal(fromGrids.selectDeviceEventBySiteGridsConfiguration);
  deviceEventGridsConfiguration = this.store.selectSignal(fromGrids.selectDeviceEventGridsConfiguration);
  deviceEventsGrids = this.store.selectSignal(fromGrids.getDeviceEventsGrids);
  defaultDeviceEventsGrid = this.store.selectSignal(fromGrids.getDefaultDeviceEventsGrid);
  calendarsGridsConfiguration = this.store.selectSignal(fromGrids.selectCalendarsGridsConfiguration);
  calendarsGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectCalendarsGrids);
  defaultCalendarsGrid: Signal<I4BGrid<I4BGridOptions, I4BGridData>> = this.store.selectSignal(fromGrids.selectDefaultCalendarsGrid);
  defaultCalendarsGrid$: Observable<I4BGrid<I4BGridOptions, I4BGridData>> = toObservable(this.defaultCalendarsGrid);
  connectorsGridsConfiguration = this.store.selectSignal(fromGrids.selectConnectorsGridsConfiguration);
  connectorsGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectConnectorsGrids);
  emailTemplatesGridsConfiguration = this.store.selectSignal(fromGrids.selectEmailTemplatesGridsConfiguration);
  emailTemplatesGrids = this.store.selectSignal(fromGrids.selectEmailTemplatesGrids);
  poEventRulesGridsConfiguration = this.store.selectSignal(fromGrids.selectPoEventRulesGridsConfiguration);
  poEventRulesGrids = this.store.selectSignal(fromGrids.selectPoEventRulesGrids);
  escalationProtocolsGridsConfiguration = this.store.selectSignal(fromGrids.selectEscalationProtocolsGridsConfiguration);
  escalationProtocolsGrids = this.store.selectSignal(fromGrids.selectEscalationProtocolsGrids);
  topicsGridsConfiguration = this.store.selectSignal(fromGrids.selectTopicsGridsConfiguration);
  topicsGrids = this.store.selectSignal(fromGrids.selectTopicsGrids);
  productsGridsConfiguration = this.store.selectSignal(fromGrids.selectProductsGridsConfiguration);
  defaultProductsGrid = this.store.selectSignal(fromGrids.getDefaultProductsGrid);
  productsGrids = this.store.selectSignal(fromGrids.selectProductsGrids);
  teamPlanningsGridsConfiguration = this.store.selectSignal(fromGrids.selectTeamPlanningsGridsConfiguration);
  teamPlanningsGrids = this.store.selectSignal(fromGrids.selectTeamPlanningsGrids);
  usersGrids = this.store.selectSignal(fromGrids.selectUsersGrids);
  usersGridsConfiguration = this.store.selectSignal(fromGrids.selectUsersGridsConfiguration);
  schedulersGridsConfiguration = this.store.selectSignal(fromGrids.selectSchedulersGridsConfiguration);
  schedulersGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectSchedulersGrids);
  defaultSchedulersGrid: Signal<I4BGrid<I4BGridOptions, I4BGridData>> = this.store.selectSignal(fromGrids.selectDefaultSchedulersGrid);
  xmqttDevicesGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectXmqttDevicesGridsConfiguration);
  xmqttDevicesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectXmqttDevicesGrids);
  nteDevicesGridConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectNteDevicesGridsConfiguration);
  nteDevicesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectNteDevicesGrids);
  ntePeripheralsGridConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectNtePeripheralsGridsConfiguration);
  ntePeripheralsGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectNtePeripheralsGrids);
  nteDeviceModelsGridConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectNteDeviceModelsGridsConfiguration);
  nteDeviceModelsGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectNteDeviceModelsGrids);
  nteDeviceTemplatesGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectNteDeviceTemplatesGridsConfiguration);
  nteDeviceTemplatesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectNteDeviceTemplatesGrids);

  bamDevicesGridConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectBamDevicesGridsConfiguration);
  bamDevicesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectBamDevicesGrids);
  bamDeviceTemplatesGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectBamDeviceTemplatesGridsConfiguration);
  bamDeviceTemplatesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectBamDeviceTemplatesGrids);
  daliaDevicesConfigurationGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectDaliaDevicesConfigurationGridsConfiguration);
  daliaDevicesConfigurationGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectDaliaDevicesConfigurationGrids);
  daliaDeviceTemplatesGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectDaliaDeviceTemplatesGridsConfiguration);
  daliaDeviceTemplatesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectDaliaDeviceTemplatesGrids);
  daliaDevicesGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectDaliaDevicesGridsConfiguration);
  daliaDevicesGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectDaliaDevicesGrids);
  daliaSensorsGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectDaliaSensorsGridsConfiguration);
  daliaSensorsGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectDaliaSensorsGrids);
  daliaFirmwaresGridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = this.store.selectSignal(fromGrids.selectDaliaFirmwaresGridsConfiguration);
  daliaFirmwaresGrids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]> = this.store.selectSignal(fromGrids.selectDaliaFirmwaresGrids);

  getDefaultGrid(masterview: string) {
    return this.store.select(fromGrids.selectDefaultGridByMasterview(masterview));
  }

  getTotalDefaultGrid(masterview: string): Observable<number> {
    return this.store.select(fromGrids.selectTotalByGrid(masterview));
  }

  getLoadedDefaultGrid(masterview: string): Observable<boolean> {
    return this.store.select(fromGrids.selectDataLoadedByGrid(masterview));
  }

  getSortDefaultGrid(masterview: string): Observable<I4BGridSort[]> {
    return this.store.select(fromGrids.selectSortByGrid(masterview));
  }

  setCustomGridOptions(masterView: string, options: CustomGridOptions): void {
    this.store.dispatch(GridsDbActions.setCustomGridOptions({ masterView, options }));
  }

  selectGridAndLoadData(gridId: string, masterview: string, filters: Filter[] = [], endPoint?: string): void {
    this.store.dispatch(GridsDbActions.selectGridAndLoadData({ gridId, masterview, filters, endPoint }));
  }

  reloadGridData(request: CommonApiRequest): void {
    this.store.dispatch(GridsDbActions.loadGridData({ request }));
  }

  reloadGridDataByGrid(grid: I4BGrid<I4BGridOptions, I4BGridData>, filters?: Filter[]): void {
    const request = getGridApiRequestByGrid(grid, filters ?? get(grid, ['gridOptions', 'filters'], []));
    this.reloadGridData(request);
  }

  loadGridDetails(grid: I4BGrid<I4BGridOptions, I4BGridData>) {
    this.store.dispatch(GridsDbActions.loadGridDetails({ concept: grid.masterview, gridId: grid.id }));
  }

  changeGridPage(grid: I4BGrid<I4BGridOptions, I4BGridData>, filters: Filter[], pagination: Pagination): void {
    const request = getGridApiRequestByGrid(grid, filters, pagination);
    this.store.dispatch(GridsDbActions.changeGridPage({ request }));
  }

  selectCurrentMaterView(masterview: string): void {
    this.store.dispatch(GridsDbActions.selectCurrentMaterView({ masterview }));
  }

  selectItemInGridData(gridId: string, itemId: string): void {
    this.store.dispatch(GridsDbActions.selectItemInGridData({ gridId, itemId }));
  }

  updateSilentGrid(grid: I4BGrid<I4BGridOptions, I4BGridData>): void {
    this.store.dispatch(GridsDbActions.updateSilentGrid({ toUpdate: grid }));
  }

  sortGridData(gridId: string, gridSort: I4BGridSort[]) {
    this.store.dispatch(GridsDbActions.sortGridData({ gridId, gridSort }));
  }

  setRowGroupsState(gridId: string, nodeId: string, expanded: boolean): void {
    this.store.dispatch(GridsDbActions.setRowGroupsState({ gridId, nodeId, expanded }));
  }

  setFilterState(gridId: string, filterState: FilterModel): void {
    this.store.dispatch(GridsDbActions.setFilterState({ gridId, filterState }));
  }

  resetFilterState(): void {
    this.store.dispatch(GridsDbActions.resetFilterState());
  }

  resetState(): void {
    this.store.dispatch(GridsDbActions.resetState());
  }

  setPreviousUrl(previousUrl: string): void {
    this.store.dispatch(GridsDbActions.setPreviousUrl({ previousUrl }));
  }

  addItemInGridData(gridId: string, item: CommonGenericModel): void {
    this.store.dispatch(GridsDbActions.addItemInGridData({ gridId, item }));
  }

  updateItemInGridData(gridId: string, item: CommonGenericModel): void {
    this.store.dispatch(GridsDbActions.updateItemInGridData({ gridId, item }));
  }

  updateItemInAllGridsData(updatedItem: CommonGenericModel): void {
    this.store.dispatch(GridsDbActions.updateItemInAllGridsData({ updatedItem }));
  }

  removeItemInGridData(gridId: string, item: CommonGenericModel): void {
    this.store.dispatch(GridsDbActions.removeItemInGridData({ gridId, item }));
  }

  getDefaultGridByConcept(concept: string) {
    this.store.dispatch(GridsDbActions.getDefaultGridByConcept({ concept }));
  }

  loadGrids(concept?: string): void {
    this.store.dispatch(GridsDbActions.loadGrids({ concept }));
  }

  addGridSuccess(grid: I4BGrid<I4BGridOptions, I4BGridData>): void {
    this.store.dispatch(GridsDbActions.addGridSuccess({ grid }));
  }

  updateGridSuccess(grid: I4BGrid<I4BGridOptions, I4BGridData>): void {
    this.store.dispatch(GridsDbActions.updateGridSuccess({ grid }));
  }

  removeGridSuccess(grid: I4BGrid<I4BGridOptions, I4BGridData>): void {
    this.store.dispatch(GridsDbActions.removeGridSuccess({ removed: grid }));
  }

  toggleRefreshActivated(refreshActivated: boolean): void {
    this.store.dispatch(GridsDbActions.toggleRefreshActivated({ refreshActivated }));
  }
}
